import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, notificationsAPI, posRequestsAPI, pincodeAPI, authAPI } from '../lib/api';
import { toast } from 'sonner';
import { User, MapPin, Package, Bell, Store, Plus, Check, X, Truck, Edit, Save, Lock, ShoppingBag } from 'lucide-react';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    addresses: [],
  });

  const [newAddress, setNewAddress] = useState({
    label: 'Home',
    name: '',
    phone: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false,
  });


  const [showPosDialog, setShowPosDialog] = useState(false);
  const [posRequest, setPosRequest] = useState({
    userType: 'customer',
    business_name: '',
    reason: '',
    nearLocality: false,
    address: '',
    city: '',
    state: '',
    pincode: '',
  });


  /* Consolidated into handleRequestSeller */

  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [pincodeValid, setPincodeValid] = useState(null);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        addresses: user.addresses || [],
      });
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [ordersRes, notifRes] = await Promise.all([
        ordersAPI.getUserOrders(),
        notificationsAPI.getUser(),
      ]);
      setOrders(ordersRes.data || []);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile({ name: profile.name, email: profile.email });
      toast.success('Profile updated');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Please fill all password fields');
      return;
    }

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const verifyPincode = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const response = await pincodeAPI.verify(pincode);
        // Response format is now from DelhiveryService: { serviceable: bool, city: str, state: str, ... }
        setPincodeValid(response.data);
        if (response.data.serviceable) {
          toast.success(`Serviceable: ${response.data.city}, ${response.data.state}`);
          setNewAddress(prev => ({
            ...prev,
            city: response.data.city || prev.city,
            state: response.data.state || prev.state
          }));
        } else {
          toast.error('This pincode is not serviceable');
        }
      } catch (error) {
        setPincodeValid(null);
      }
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.name || !newAddress.line1 || !newAddress.city || !newAddress.pincode) {
      toast.error('Please fill required fields');
      return;
    }

    const updatedAddresses = [...profile.addresses, { ...newAddress, id: Date.now().toString() }];

    try {
      await updateProfile({ addresses: updatedAddresses });
      setProfile({ ...profile, addresses: updatedAddresses });
      setShowAddressDialog(false);
      setNewAddress({
        label: 'Home',
        name: '',
        phone: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        pincode: '',
        isDefault: false,
      });
      toast.success('Address added');
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const handleRemoveAddress = async (addressId) => {
    const updatedAddresses = profile.addresses.filter(a => a.id !== addressId);
    try {
      await updateProfile({ addresses: updatedAddresses });
      setProfile({ ...profile, addresses: updatedAddresses });
      toast.success('Address removed');
    } catch (error) {
      toast.error('Failed to remove address');
    }
  };


  const handleRequestPos = async () => {

    // Validation: 
    // If SELLER: require business_name, reason AND location details
    // If NOT Near Locality: require location details
    if (posRequest.userType === 'seller') {
      if (!posRequest.business_name || !posRequest.reason) {
        toast.error('Please provide business name and reason for request');
        return;
      }
      if (!posRequest.address || !posRequest.city || !posRequest.state || !posRequest.pincode) {
        toast.error('Please provide complete location details');
        return;
      }
    } else if (!posRequest.nearLocality) {
      if (!posRequest.address || !posRequest.city || !posRequest.state || !posRequest.pincode) {
        toast.error('Please provide complete location details');
        return;
      }
    }

    if ((posRequest.userType === 'seller' || !posRequest.nearLocality) && posRequest.pincode && posRequest.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }

    try {
      const response = await posRequestsAPI.request(posRequest);
      if (response.data?.auto_approved) {
        toast.success('POS access granted! You can now use takeaway functionality.');
        // Refresh page or auth context to reflect new role
        window.location.reload();
      } else {
        toast.success('POS access request submitted! Admin will review your request.');
      }
      setShowPosDialog(false);
      setPosRequest({
        userType: 'customer',
        business_name: '',
        reason: '',
        nearLocality: false,
        address: '',
        city: '',
        state: '',
        pincode: '',
      });
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to submit POS request');
    }
  };

  const handleSwitchToCustomer = async () => {
    try {
      await posRequestsAPI.switchToCustomer();
      toast.success('Switched back to normal customer!');
      window.location.reload();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to switch role');
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark read');
    }
  };

  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all read');
    }
  };

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p>Please login to view your profile</p>
        <Button onClick={() => navigate('/login')} className="mt-4 btn-primary">Login</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="profile-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold">My Account</h1>
          <p className="text-muted-foreground">Manage your profile and preferences</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Become Customer Button (Only for POS Users) */}
          {user.role === 'pos_user' && (
            <Button
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              onClick={handleSwitchToCustomer}
            >
              <User className="w-4 h-4 mr-2" />
              Become Customer
            </Button>
          )}

          {/* Request POS Access Button (Only for Customers) */}
          {user.role === 'customer' && (
            <Dialog open={showPosDialog} onOpenChange={setShowPosDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Request POS Access
                </Button>
              </DialogTrigger>

              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Request POS Access</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Get access to takeaway functionality and manage your shop efficiently.
                  </p>

                  {/* Near Locality Checkbox */}
                  <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                    <Checkbox
                      id="nearLocality"
                      checked={posRequest.nearLocality}
                      onCheckedChange={(checked) => {
                        setPosRequest({
                          ...posRequest,
                          nearLocality: checked,
                          // Reset userType to customer if unchecked, to keep it simple
                          userType: checked ? posRequest.userType : 'customer'
                        });
                      }}
                    />
                    <Label htmlFor="nearLocality" className="cursor-pointer font-normal">
                      I am from the near locality (same area as the main store)
                    </Label>
                  </div>

                  {/* User Type Selection (Only if Near Locality is checked) */}
                  {posRequest.nearLocality && (
                    <div className="space-y-3 p-4 border-2 border-primary/20 rounded-xl bg-primary/5 shadow-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-primary" />
                        <Label className="text-sm font-semibold text-primary">I am a...</Label>
                      </div>
                      <RadioGroup
                        value={posRequest.userType}
                        onValueChange={(value) => setPosRequest({ ...posRequest, userType: value })}
                        className="flex flex-col sm:flex-row gap-4"
                      >
                        <div className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${posRequest.userType === 'customer' ? 'bg-white border-primary shadow-sm' : 'hover:bg-primary/5 border-transparent'}`}>
                          <RadioGroupItem value="customer" id="type-customer" />
                          <Label htmlFor="type-customer" className="cursor-pointer font-medium">Local Customer</Label>
                        </div>
                        <div className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${posRequest.userType === 'seller' ? 'bg-white border-primary shadow-sm' : 'hover:bg-primary/5 border-transparent'}`}>
                          <RadioGroupItem value="seller" id="type-seller" />
                          <Label htmlFor="type-seller" className="cursor-pointer font-medium">Business / Seller</Label>
                        </div>
                      </RadioGroup>
                      {posRequest.userType === 'customer' && (
                        <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 p-2 rounded-md border border-emerald-100 italic">
                          <Check className="w-3 h-3" />
                          <span>As a local customer, your POS access will be approved automatically.</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Business and Reason Fields (Only for Seller or NOT Near Locality) */}
                  {(!posRequest.nearLocality || posRequest.userType === 'seller') && (
                    <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="space-y-2">
                        <Label htmlFor="pos_business_name" className="flex justify-between">
                          <span>Business/Store Name</span>
                          {posRequest.userType === 'seller' ? <span className="text-red-500 text-xs">* Required</span> : <span className="text-muted-foreground text-xs">(Optional)</span>}
                        </Label>
                        <Input
                          id="pos_business_name"
                          value={posRequest.business_name}
                          onChange={(e) => setPosRequest({ ...posRequest, business_name: e.target.value })}
                          placeholder="Your store or business name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="pos_reason" className="flex justify-between">
                          <span>Reason for Request</span>
                          {posRequest.userType === 'seller' ? <span className="text-red-500 text-xs">* Required</span> : <span className="text-muted-foreground text-xs">(Optional)</span>}
                        </Label>
                        <Input
                          id="pos_reason"
                          value={posRequest.reason}
                          onChange={(e) => setPosRequest({ ...posRequest, reason: e.target.value })}
                          placeholder="e.g., Need takeaway functionality for my store"
                        />
                      </div>
                    </div>
                  )}

                  {/* Conditional Location Fields - Shown if NOT near locality OR if user is a SELLER */}
                  {(!posRequest.nearLocality || posRequest.userType === 'seller') && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center gap-2 mb-1">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <p className="text-sm font-medium">Location Details</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="pos_address">Address *</Label>
                        <Input
                          id="pos_address"
                          required
                          value={posRequest.address}
                          onChange={(e) => setPosRequest({ ...posRequest, address: e.target.value })}
                          placeholder="Street address, building name"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="pos_city">City *</Label>
                          <Input
                            id="pos_city"
                            required
                            value={posRequest.city}
                            onChange={(e) => setPosRequest({ ...posRequest, city: e.target.value })}
                            placeholder="City"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="pos_state">State *</Label>
                          <Input
                            id="pos_state"
                            required
                            value={posRequest.state}
                            onChange={(e) => setPosRequest({ ...posRequest, state: e.target.value })}
                            placeholder="State"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="pos_pincode">Pincode *</Label>
                        <Input
                          id="pos_pincode"
                          required
                          value={posRequest.pincode}
                          onChange={(e) => setPosRequest({ ...posRequest, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                          placeholder="6-digit pincode"
                          maxLength={6}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleRequestPos}
                    className="w-full btn-primary h-11"
                    disabled={
                      (posRequest.userType === 'seller' && (!posRequest.business_name || !posRequest.reason || !posRequest.address || !posRequest.pincode)) ||
                      (!posRequest.nearLocality && (!posRequest.address || !posRequest.pincode))
                    }
                  >
                    Submit Request
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Badges */}
          {user.is_wholesale && (
            <Badge className="bg-primary/10 text-primary px-4 py-2">
              <Store className="w-4 h-4 mr-2" />
              Verified Wholesaler
            </Badge>
          )}
          {user.supplier_status === 'pending' && (
            <Badge className="bg-amber-500/20 text-amber-600 px-4 py-2">
              <Store className="w-4 h-4 mr-2" />
              Seller Request Pending
            </Badge>
          )}
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="addresses">Addresses</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="notifications" className="relative">
            Notifications
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Personal Information
              </CardTitle>
              {!editMode ? (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <Button size="sm" onClick={handleSaveProfile} className="btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    disabled={!editMode}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={profile.phone} disabled />
                  <p className="text-xs text-muted-foreground">Phone number cannot be changed</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full sm:w-auto">
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Change Password</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Current Password</Label>
                        <Input
                          type="password"
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                          placeholder="Enter current password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>New Password</Label>
                        <Input
                          type="password"
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                          placeholder="Enter new password (min 6 chars)"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Confirm New Password</Label>
                        <Input
                          type="password"
                          value={passwordForm.confirm_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                          placeholder="Confirm new password"
                        />
                      </div>
                      <Button onClick={handleChangePassword} className="w-full btn-primary">
                        Update Password
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          {/* Logout Section */}
          <Card className="mt-6 border-red-200 dark:border-red-900">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-red-600 dark:text-red-400">Logout</h3>
                  <p className="text-sm text-muted-foreground">Sign out from your account</p>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => {
                    logout();
                    navigate('/login');
                    toast.success('Logged out successfully');
                  }}
                  className="w-full sm:w-auto"
                >
                  Logout
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses" className="mt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Saved Addresses</h3>
              <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Address
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Address</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input
                          value={newAddress.name}
                          onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                          placeholder="Full name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={newAddress.phone}
                          onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                          placeholder="Phone number"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 1 *</Label>
                      <Input
                        value={newAddress.line1}
                        onChange={(e) => setNewAddress({ ...newAddress, line1: e.target.value })}
                        placeholder="House/Flat No., Building"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 2</Label>
                      <Input
                        value={newAddress.line2}
                        onChange={(e) => setNewAddress({ ...newAddress, line2: e.target.value })}
                        placeholder="Street, Area"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>City *</Label>
                        <Input
                          value={newAddress.city}
                          onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input
                          value={newAddress.state}
                          onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                          placeholder="State"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Pincode *</Label>
                        <Input
                          value={newAddress.pincode}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            if (val === newAddress.pincode) return;

                            setNewAddress({ ...newAddress, pincode: val });

                            if (val.length === 6) {
                              verifyPincode(val);
                            } else {
                              setPincodeValid(null);
                            }
                          }}
                          placeholder="6-digit"
                          className={pincodeValid?.serviceable === false ? 'border-red-500' : ''}
                        />
                        {pincodeValid && (
                          <p className={`text-xs ${pincodeValid.serviceable ? 'text-emerald-500' : 'text-red-500'}`}>
                            {pincodeValid.serviceable ? `✓ ${pincodeValid.city}, ${pincodeValid.state}` : 'Not serviceable'}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button onClick={handleAddAddress} className="w-full btn-primary">
                      Save Address
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {profile.addresses.length === 0 ? (
              <Card className="p-8 text-center">
                <MapPin className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No saved addresses</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {profile.addresses.map((address) => (
                  <Card key={address.id}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <Badge variant="outline" className="mb-2">{address.label}</Badge>
                          <p className="font-medium">{address.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {address.line1}
                            {address.line2 && `, ${address.line2}`}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {address.city}, {address.state} - {address.pincode}
                          </p>
                          {address.phone && (
                            <p className="text-sm text-muted-foreground mt-1">📞 {address.phone}</p>
                          )}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500"
                          onClick={() => handleRemoveAddress(address.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-6">
          <div className="space-y-4">
            {orders.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No orders yet</p>
                <Button onClick={() => navigate('/products')} className="mt-4 btn-primary">
                  Start Shopping
                </Button>
              </Card>
            ) : (
              orders.slice(0, 5).map((order) => (
                <Card key={order.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/orders/${order.id}`)}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">#{order.order_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString('en-IN')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-primary">₹{order.grand_total.toLocaleString()}</p>
                        <Badge className={`status-badge status-${order.status}`}>{order.status}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
            {orders.length > 5 && (
              <Button variant="outline" className="w-full" onClick={() => navigate('/orders')}>
                View All Orders
              </Button>
            )}
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications" className="mt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">Recent Notifications</h3>
              {unreadCount > 0 && (
                <Button variant="outline" size="sm" onClick={markAllRead}>
                  Mark All Read
                </Button>
              )}
            </div>

            {notifications.length === 0 ? (
              <Card className="p-8 text-center">
                <Bell className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No notifications</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {notifications.map((notif) => (
                  <Card
                    key={notif.id}
                    className={`cursor-pointer transition-colors ${!notif.read ? 'bg-primary/5 border-primary/20' : ''}`}
                    onClick={() => {
                      markNotificationRead(notif.id);
                      if (notif.data?.order_id) navigate(`/orders/${notif.data.order_id}`);
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex gap-3">
                          <div className={`p-2 rounded-full ${!notif.read ? 'bg-primary/10' : 'bg-muted'}`}>
                            {notif.type === 'order_placed' && <Package className="w-4 h-4 text-primary" />}
                            {notif.type === 'order_update' && <Truck className="w-4 h-4 text-blue-500" />}
                            {notif.type === 'seller_approved' && <Check className="w-4 h-4 text-emerald-500" />}
                            {!['order_placed', 'order_update', 'seller_approved'].includes(notif.type) && <Bell className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="font-medium">{notif.title}</p>
                            <p className="text-sm text-muted-foreground">{notif.message}</p>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(notif.created_at).toLocaleDateString('en-IN')}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
